import { type FlowChartEdge } from '@precis-digital/kurama/charts/FlowChart'
import { type BadgeColor } from '@precis-digital/kurama/Badge'
import {
  API_CONFIG_STATUSES,
  type DagService,
  type DagChildrenStatusItem,
  type DagStatusResp,
  type E2EStatus
} from './api/analytics'
import { objectKeys } from './utils'
import { logAndReportError } from 'shared/errorReporter'

export const STATUSES = {
  active: 'active',
  paused: 'paused',
  error: 'error',
  running: 'running',
  new: 'new',
  connected: 'connected',
  disconnected: 'disconnected',
  pending: 'pending',
  disabled: 'disabled'
} as const

export type ConfigStatus = keyof typeof STATUSES

export const STATUS_COLOR_MAP: Partial<Record<ConfigStatus, BadgeColor>> = {
  active: 'success',
  running: 'warning',
  new: 'info',
  pending: 'info',
  error: 'error',
  connected: 'success',
  disconnected: 'error'
}

export const STATUSES_WITH_TOOLTIP: ConfigStatus[] = [
  STATUSES.error,
  STATUSES.running,
  STATUSES.active,
  STATUSES.connected,
  STATUSES.disconnected,
  STATUSES.pending,
  STATUSES.paused,
  STATUSES.disabled
]

export const ORDERED_TAB_STATUSES = [STATUSES.active, STATUSES.paused, STATUSES.running, STATUSES.error]

export const getStatusFromAPIStatusData = (runSchedule: boolean, e2eStatus: E2EStatus | undefined): ConfigStatus => {
  if (runSchedule) {
    if (e2eStatus == null || e2eStatus === API_CONFIG_STATUSES.fetch_failed) {
      return STATUSES.pending
    } else if (e2eStatus === API_CONFIG_STATUSES.completed) {
      return STATUSES.active
    } else if (e2eStatus === API_CONFIG_STATUSES.running || e2eStatus === API_CONFIG_STATUSES.queued) {
      return STATUSES.running
    }
  } else {
    return STATUSES.paused
  }

  return STATUSES.error
}

/***
 MW 2024-05-09

 Due to unreliability in the Marketing Evaluation Pipeline, we need to change
 the status of the pipeline to running if the status is ERR999 (An unknown / contact alvie support error)
 This givest the engineering team more time to investigate the issue and fix it without the pipeline alerting the user.
 hopefully, we can remove this in the future.
 ***/
export const getStatusMarketingEvaluation = (
  runSchedule: boolean,
  e2eStatus?: E2EStatus,
  errorCode?: ErrorCodeType | null
): ConfigStatus => {
  const defaultStatus = getStatusFromAPIStatusData(runSchedule, e2eStatus)
  if (errorCode === defaultErrorCode) {
    return STATUSES.running
  }
  return defaultStatus
}

const ErrorCode = {
  ERR100: 'ERR100',
  ERR200: 'ERR200',
  ERR300: 'ERR300',
  ERR002: 'ERR002',
  ERR003: 'ERR003',
  ERR004: 'ERR004',
  ERR005: 'ERR005',
  ERR007: 'ERR007',
  ERR999: 'ERR999',
  ERR101: 'ERR101',
  ERR102: 'ERR102',
  ERR103: 'ERR103',
  ERR104: 'ERR104',
  ERR105: 'ERR105',
  ERR106: 'ERR106',
  ERR107: 'ERR107',
  ERR109: 'ERR109',
  ERR110: 'ERR110',
  ERR111: 'ERR111',
  ERR201: 'ERR201',
  ERR202: 'ERR202',
  ERR203: 'ERR203',
  ERR204: 'ERR204',
  ERR205: 'ERR205',
  ERR206: 'ERR206',
  ERR302: 'ERR302',
  ERR303: 'ERR303',
  ERR304: 'ERR304',
  ERR305: 'ERR305',
  ERR306: 'ERR306',
  ERR307: 'ERR307',
  ERR308: 'ERR308',
  ERR401: 'ERR401',
  ERR402: 'ERR402',
  ERR403: 'ERR403',
  ERR404: 'ERR404',
  ERR405: 'ERR405',
  ERR406: 'ERR406',
  ERR407: 'ERR407',
  ERR408: 'ERR408',
  ERR409: 'ERR409',
  ERR410: 'ERR410',
  ERR411: 'ERR411',
  ERR412: 'ERR412',
  ERR413: 'ERR413',
  ERR414: 'ERR414',
  ERR415: 'ERR415',
  ERR416: 'ERR416',
  ERR417: 'ERR417',
  ERR418: 'ERR418',
  ERR419: 'ERR419',
  ERR420: 'ERR420',
  ERR421: 'ERR421',
  ERR422: 'ERR422',
  ERR423: 'ERR423'
} as const
export type ErrorCodeType = (typeof ErrorCode)[keyof typeof ErrorCode]

export const defaultErrorCode: ErrorCodeType = ErrorCode.ERR999

export type IntercomErrorId =
  | 206420
  | 206895
  | 206843
  | 206574
  | 206896
  | 206897
  | 206844
  | 206845
  | 206575
  | 206576
  | 206898
  | 206421
  | 206422
  | 206423
  | 206424
  | 183187
  | 206899
  | 206900
  | 206846
  | 206847
  | 206976
  | 212382
  | 215279
  | 215282
  | 272285
  | 272292
  | 272296
  | 272299
  | 272452
  | 272455
  | 272456
  | 272458
  | 382087
  | 382103
  | 382104
  | 382106
  | 382107
  | 382108
  | 382110
  | 382111
  | 382112
  | 534203
  | 543915
  | 543917
  | 372884
  | 390281
  | 390257
  | 390274
  | 449713
  | 496516
  | 546020

export const DEFAULT_ERROR_ARTICLE_ID: IntercomErrorId = 206976

export type TranslationPlaceholder = 'targetGCPProjectID' | 'pipelineLabel' | 'errorMessage'

export type ErrorTranslationPlaceholders = Partial<Record<TranslationPlaceholder, string>>

const PIPELINE_LABEL_ERROR_CODES = [ErrorCode.ERR003, ErrorCode.ERR005, ErrorCode.ERR007] as const
type PipelineLabelErrorCode = (typeof PIPELINE_LABEL_ERROR_CODES)[number]

export const isPipelineLabelErrorCode = (code: ErrorCodeType): code is PipelineLabelErrorCode =>
  (PIPELINE_LABEL_ERROR_CODES as readonly ErrorCodeType[]).includes(code)

const TARGET_GCP_PROJECT_ERROR_CODES = [
  ErrorCode.ERR103,
  ErrorCode.ERR104,
  ErrorCode.ERR105,
  ErrorCode.ERR106,
  ErrorCode.ERR107,
  ErrorCode.ERR206
] as const
type TargetGcpProjectErrorCode = (typeof TARGET_GCP_PROJECT_ERROR_CODES)[number]

export const isTargetGcpProjectErrorCode = (code: ErrorCodeType): code is TargetGcpProjectErrorCode =>
  (TARGET_GCP_PROJECT_ERROR_CODES as readonly ErrorCodeType[]).includes(code)

export const ERROR_CODE_DETAILS_MAP: Record<
  ErrorCodeType,
  {
    translationPlaceholders: TranslationPlaceholder[]
    intercomArticleId: IntercomErrorId
  }
> = {
  [ErrorCode.ERR100]: { translationPlaceholders: [], intercomArticleId: 206420 },
  [ErrorCode.ERR200]: { translationPlaceholders: [], intercomArticleId: 206895 },
  [ErrorCode.ERR300]: { translationPlaceholders: [], intercomArticleId: 206843 },
  [ErrorCode.ERR002]: { translationPlaceholders: [], intercomArticleId: 206896 },
  [ErrorCode.ERR003]: { translationPlaceholders: ['pipelineLabel'], intercomArticleId: 206897 },
  [ErrorCode.ERR004]: { translationPlaceholders: [], intercomArticleId: 206844 },
  [ErrorCode.ERR005]: { translationPlaceholders: ['pipelineLabel'], intercomArticleId: 206845 },
  [ErrorCode.ERR007]: { translationPlaceholders: ['pipelineLabel'], intercomArticleId: 206576 },
  [ErrorCode.ERR101]: { translationPlaceholders: [], intercomArticleId: 206898 },
  [ErrorCode.ERR102]: { translationPlaceholders: [], intercomArticleId: 206421 },
  [ErrorCode.ERR103]: { translationPlaceholders: ['targetGCPProjectID'], intercomArticleId: 206422 },
  [ErrorCode.ERR104]: { translationPlaceholders: ['targetGCPProjectID'], intercomArticleId: 206423 },
  [ErrorCode.ERR105]: { translationPlaceholders: ['targetGCPProjectID'], intercomArticleId: 206424 },
  [ErrorCode.ERR106]: { translationPlaceholders: ['targetGCPProjectID'], intercomArticleId: 183187 },
  [ErrorCode.ERR107]: { translationPlaceholders: ['targetGCPProjectID'], intercomArticleId: 212382 },
  [ErrorCode.ERR109]: { translationPlaceholders: [], intercomArticleId: 390274 },
  [ErrorCode.ERR110]: { translationPlaceholders: [], intercomArticleId: 390257 },
  [ErrorCode.ERR111]: { translationPlaceholders: [], intercomArticleId: 496516 },
  [ErrorCode.ERR201]: { translationPlaceholders: ['errorMessage'], intercomArticleId: 206899 },
  [ErrorCode.ERR202]: { translationPlaceholders: [], intercomArticleId: 206900 },
  [ErrorCode.ERR203]: { translationPlaceholders: [], intercomArticleId: 215279 },
  [ErrorCode.ERR204]: { translationPlaceholders: [], intercomArticleId: 215282 },
  [ErrorCode.ERR205]: { translationPlaceholders: [], intercomArticleId: 372884 },
  [ErrorCode.ERR206]: { translationPlaceholders: ['targetGCPProjectID'], intercomArticleId: 390281 },
  [ErrorCode.ERR302]: { translationPlaceholders: [], intercomArticleId: 206846 },
  [ErrorCode.ERR303]: { translationPlaceholders: [], intercomArticleId: 206847 },
  [ErrorCode.ERR304]: { translationPlaceholders: [], intercomArticleId: 449713 },
  [ErrorCode.ERR305]: { translationPlaceholders: [], intercomArticleId: 449713 },
  [ErrorCode.ERR306]: { translationPlaceholders: [], intercomArticleId: 449713 },
  [ErrorCode.ERR307]: { translationPlaceholders: [], intercomArticleId: 449713 },
  [ErrorCode.ERR308]: { translationPlaceholders: [], intercomArticleId: 449713 },
  [ErrorCode.ERR401]: { translationPlaceholders: [], intercomArticleId: 272285 },
  [ErrorCode.ERR402]: { translationPlaceholders: [], intercomArticleId: 272292 },
  [ErrorCode.ERR403]: { translationPlaceholders: [], intercomArticleId: 272296 },
  [ErrorCode.ERR404]: { translationPlaceholders: [], intercomArticleId: 272299 },
  [ErrorCode.ERR405]: { translationPlaceholders: [], intercomArticleId: 206575 },
  [ErrorCode.ERR406]: { translationPlaceholders: [], intercomArticleId: 206574 },
  [ErrorCode.ERR407]: { translationPlaceholders: [], intercomArticleId: 272452 },
  [ErrorCode.ERR408]: { translationPlaceholders: [], intercomArticleId: 272455 },
  [ErrorCode.ERR409]: { translationPlaceholders: [], intercomArticleId: 272456 },
  [ErrorCode.ERR410]: { translationPlaceholders: [], intercomArticleId: 272458 },
  [ErrorCode.ERR411]: { translationPlaceholders: [], intercomArticleId: 382087 },
  [ErrorCode.ERR412]: { translationPlaceholders: [], intercomArticleId: 382103 },
  [ErrorCode.ERR413]: { translationPlaceholders: [], intercomArticleId: 382104 },
  [ErrorCode.ERR414]: { translationPlaceholders: [], intercomArticleId: 382106 },
  [ErrorCode.ERR415]: { translationPlaceholders: [], intercomArticleId: 382107 },
  [ErrorCode.ERR416]: { translationPlaceholders: [], intercomArticleId: 382108 },
  [ErrorCode.ERR417]: { translationPlaceholders: [], intercomArticleId: 382110 },
  [ErrorCode.ERR418]: { translationPlaceholders: [], intercomArticleId: 382111 },
  [ErrorCode.ERR419]: { translationPlaceholders: [], intercomArticleId: 382112 },
  [ErrorCode.ERR420]: { translationPlaceholders: [], intercomArticleId: 534203 },
  [ErrorCode.ERR421]: { translationPlaceholders: [], intercomArticleId: 543915 },
  [ErrorCode.ERR422]: { translationPlaceholders: [], intercomArticleId: 543917 },
  [ErrorCode.ERR423]: { translationPlaceholders: [], intercomArticleId: 546020 },
  [ErrorCode.ERR999]: { translationPlaceholders: [], intercomArticleId: 206976 }
}

/** Returns a valid error code key that is safe to use in translations.
 *  Falls back to ERR999 when the provided code is null/undefined or not mapped. */
export const getSafeErrorCode = (code?: string | null): ErrorCodeType => {
  if (code != null && Object.hasOwn(ERROR_CODE_DETAILS_MAP, code)) {
    return code as ErrorCodeType
  }
  return defaultErrorCode
}

export interface ErrorDetails {
  translationPlaceholders: ErrorTranslationPlaceholders
  intercomArticleId: IntercomErrorId
}

/** Retrieves error details for a given error code with optional placeholder values.
 * Returns null if errorCode is null/undefined, otherwise returns error details
 * with resolved placeholders for translation. */
export const getErrorCodeDetails = (
  errorCode?: ErrorCodeType | null,
  errorMessage?: string | null,
  targetGCPProjectID?: string | null,
  pipelineLabel?: string | null
): ErrorDetails | null => {
  if (errorCode === undefined || errorCode === null) {
    return null
  }
  return getErrorCodeDetailsWithPlaceholders(errorCode, {
    targetGCPProjectID,
    pipelineLabel,
    errorMessage
  })
}

type PlaceholderValues = Partial<Record<TranslationPlaceholder, string | null>>

/** Resolves error details with placeholder values substituted into translation strings.
 * If any required placeholder values are missing, falls back to ERR999 default error
 * and logs an error for debugging. */
const getErrorCodeDetailsWithPlaceholders = (
  errorCode: ErrorCodeType,
  placeholderValues: PlaceholderValues
): ErrorDetails => {
  const { translationPlaceholders, intercomArticleId } =
    ERROR_CODE_DETAILS_MAP[errorCode] ?? ERROR_CODE_DETAILS_MAP[defaultErrorCode]

  const resolvedPlaceholders: ErrorTranslationPlaceholders = {}
  const missingPlaceholders: TranslationPlaceholder[] = []

  for (const placeholder of translationPlaceholders) {
    const value = placeholderValues[placeholder]

    if (value != null) {
      resolvedPlaceholders[placeholder] = value
    } else {
      missingPlaceholders.push(placeholder)
    }
  }

  if (missingPlaceholders.length > 0) {
    const errorMessage = [
      `Missing placeholder values for error ${errorCode}`,
      `Missing: ${missingPlaceholders.join(', ')}`,
      `Required: ${translationPlaceholders.join(', ')}`,
      `Fallback: ${defaultErrorCode}`
    ].join(' | ')
    logAndReportError(Error(errorMessage))

    return {
      translationPlaceholders: {},
      intercomArticleId: ERROR_CODE_DETAILS_MAP[defaultErrorCode].intercomArticleId
    }
  }

  return {
    translationPlaceholders: resolvedPlaceholders,
    intercomArticleId
  }
}

export interface DagNode {
  id: string
  position: { x: number; y: number }
  data: {
    status: E2EStatus
    lastUpdated: string | null
    configId: string
    service: DagService
    message: string
    error: string
    errorCode: ErrorCodeType | null
    errorMessage: string | null
  }
  level: number
}

export const transformDAGStatusToNodesAndEdges = (
  dagStatus: DagStatusResp
): { nodes: DagNode[]; edges: FlowChartEdge[] } => {
  const nodes: DagNode[] = []
  const edges: FlowChartEdge[] = []
  const queue: Array<{ node: DagStatusResp | DagChildrenStatusItem; parentId: string | null; level: number }> = [
    { node: dagStatus, parentId: null, level: 0 }
  ]
  const levels: Record<number, DagNode[]> = {}

  while (queue.length > 0) {
    const firstItemInQueue = queue.shift()

    if (firstItemInQueue == null) break

    const { node, parentId, level } = firstItemInQueue

    levels[level] ??= []

    const nodeId = `${node.configId}-${node.service}`
    const nodeData: DagNode = {
      id: nodeId,
      data: {
        service: node.service,
        status: node.status,
        lastUpdated: node.lastUpdated,
        configId: node.configId,
        message: node.message,
        error: node.error,
        errorCode: node.errorCode,
        errorMessage: node.errorMessage
      },
      position: { x: -level * 400, y: levels[level].length * 100 },
      level
    }
    nodes.push(nodeData)
    levels[level].push(nodeData)

    if (parentId !== null) {
      const edgeData = {
        id: `${nodeId}-${parentId}`,
        source: nodeId,
        target: parentId
      }
      edges.push(edgeData)
    }

    if (node.children != null) {
      if (Array.isArray(node.children)) {
        for (const child of node.children) {
          queue.push({ node: child, parentId: nodeId, level: level + 1 })
        }
      } else {
        queue.push({ node: node.children, parentId: nodeId, level: level + 1 })
      }
    }
  }

  const levelKeys = objectKeys(levels)

  let highestAverageYLevelKey = 0
  let highestAverageY = 0

  levelKeys.forEach((key) => {
    let avgYLevel = 0

    const level = levels[key]

    avgYLevel = level.reduce((sum, node) => sum + node.position.y, 0) / level.length

    if (avgYLevel > highestAverageY) {
      highestAverageY = avgYLevel
      highestAverageYLevelKey = key
    }
  })

  for (const key of levelKeys) {
    const levelNodes = levels[key]
    const middleIndex = (levels[highestAverageYLevelKey].length - 1) / 2
    for (let index = 0; index < levelNodes.length; index++) {
      const node = levelNodes[index]
      node.position.y =
        key !== highestAverageYLevelKey ? highestAverageY + (middleIndex + index) * 100 : highestAverageY + index * 100
    }
  }

  return { nodes, edges }
}
